import { CircularProgress } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type RowData,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useAsyncRetry } from 'react-use';
import { useSnapshot } from 'valtio';
import {
  addSingleRequestFile as apiAddSingleRequestFile,
  getDataRequest as apiGetDataRequest,
  getDataRequestsForProject as apiGetProjectRequests,
} from '~/api/civilsearch';
import { constructFile } from '~/common/miscUtils';
import { type CivilGridFile, FileCategory } from '~/common/types';
import UploadButton from '~/components2/forms/UploadButton';
import { Notify } from '~/components2/Notify';
import CivilSearchTable from '~/pages/admin/civilsearch/common/CivilSearchTable';
import store from '../../store';
// use the "supporting files" columns since they are the same.
import { columns } from './columns';
import evidenceStore from './store';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    refreshData?: () => void;
  }
}

export default function Evidence() {
  const [data, setData] = useState<CivilGridFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { requestId } = useParams({ strict: false }) as { requestId: string };
  const { project } = useSnapshot(store);

  const query = useAsyncRetry(async () => {
    const [dataRequest, projectRequests] = await Promise.all([
      await apiGetDataRequest(requestId),
      await apiGetProjectRequests(project!.id),
    ]);

    setData([...dataRequest.files]);
    evidenceStore.setRequests(projectRequests);
  }, [requestId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    return () => evidenceStore.reset();
  }, [requestId]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    initialState: {
      sorting: [{ id: 'createdAt', desc: false }],
    },
    meta: { refreshData: query.retry },
  });

  const onUpload = async (files: File[]) => {
    try {
      setIsProcessing(true);

      const uploads = files.map(async (f) => {
        const data = constructFile(f, {
          category: FileCategory.DataRequestFile,
        });

        const file = await apiAddSingleRequestFile(project!.id, requestId, data);

        return file;
      });

      await Promise.all(uploads);
      Notify.info({ message: 'Files successfully uploaded' });
      query.retry();
    } catch {
      Notify.error({ message: 'Error uploading files' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (query.loading) return <CircularProgress indeterminate />;
  if (query.error) {
    console.error(query.error);
    return (
      <div className="h-full w-full grow">
        <div>An error occurred while fetching files.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="my-1">
        <span className="font-semibold">NOTE: </span>
        File naming conventions should follow{' '}
        <span className="font-semibold">AgencyName_AssetTypeDataType_ProjectID</span> (Ex:
        CitySacramento_SewerMaps_DCZ9SD)
      </div>
      <CivilSearchTable id="evidence-table" table={table}>
        <UploadButton isProcessing={isProcessing} disabled={isProcessing} multiple onUpload={onUpload}>
          Upload Files
        </UploadButton>
      </CivilSearchTable>
    </div>
  );
}
